import { Observable } from 'rxjs';

/**
 * Playback/live toolbar contract shared by every AIS sensor.
 *
 * A sensor's layer service (AISstream) or a dedicated controller (Voyage Radar)
 * implements this so the pluggable toolbar controls can drive playback without
 * knowing anything about the underlying operator, transport, or rendering.
 */

export type PlaybackMode = 'live' | 'playback' | 'idle';

/** One entry per ship-type category present in the loaded data (for filter chips). */
export interface PlaybackAvailableType {
  code: string;
  label: string;
  color: string;
  count: number;
}

/** Immutable snapshot of everything the toolbar controls need to render. */
export interface PlaybackState {
  mode: PlaybackMode;
  isPlaying: boolean;
  currentIndex: number;
  stepCount: number;
  /** ISO/UTC label per step — drives the timeline timestamp + slider ticks. */
  stepLabels: string[];
  /** Simulated-seconds per real-second (playback), or step interval. */
  speed: number;
  showLabels: boolean;
  /**
   * Group nearby vessels into count bubbles instead of drawing every hull.
   *
   * Optional, like the track fields below: `AisstreamLayerService` builds its
   * state as a bare literal, and it draws its own markers rather than going
   * through the Voyage Radar display pass, so it has no clustering to report.
   */
  clusterVessels?: boolean;
  /** Empty set = show all categories. */
  typeFilter: Set<string>;
  availableTypes: PlaybackAvailableType[];
  /** ISO timestamp of the latest data snapshot — used by the live 'last updated' control. */
  lastUpdated?: string | null;
  /**
   * True while a REFRESH is in flight — a paid pull of the latest positions.
   *
   * Deliberately not shared with the playback load below. The ON MAP bar renders
   * this as "REFRESHING · Pulling the latest vessel positions", which a replay of
   * already-saved snapshots is not: it costs nothing, changes nothing on the
   * operator's side, and can run for many seconds over a large archive.
   */
  loading?: boolean;

  // ── Voyage Radar snapshot playback ───────────────────────────────────────────
  /**
   * True while the saved refresh snapshots for a replay are being fetched.
   *
   * The truth, and what every gate reads — a second entry while N frames are in
   * the air would start a competing load over the same window.
   */
  playbackLoading?: boolean;
  /**
   * `playbackLoading`, delayed — what the UI should DRAW.
   *
   * Same reasoning as `trackLoadingVisible`: a cached window re-enters in a few
   * milliseconds, and drawing the honest flag would blink a spinner on exactly
   * those. See `setPlaybackLoading`.
   */
  playbackLoadingVisible?: boolean;
  /** What the in-flight replay is FOR, so the wait can name it. Null when idle. */
  pendingPlayback?: PendingPlaybackRef | null;
  /**
   * refreshId of the frame currently on screen, or null outside playback.
   *
   * Published so the Refresh History list can mark the snapshot being played the
   * way the Vessel Tracks list marks the track being drawn — without it the panel
   * sits still through the one thing it exists to narrate.
   */
  playbackRefreshId?: string | null;
  /**
   * Snapshots that were in the window but did not load (0 when all arrived).
   *
   * Surfaced rather than swallowed: a replay quietly missing a fifth of its
   * frames looks exactly like a replay of an archive that was always that size.
   */
  playbackSkipped?: number;

  // ── Voyage Radar track mode ──────────────────────────────────────────────────
  // Every field below is OPTIONAL by design: AisstreamLayerService builds its
  // state as a bare object literal (no EMPTY_PLAYBACK_STATE spread), so a
  // required field here would break its compile.
  /** MMSI of the vessel currently selected on the map (null = none). */
  activeMmsi?: string | null;
  /** Display name of the active vessel (falls back to its MMSI). */
  activeVesselName?: string | null;
  /** The active vessel's own colour — drives the halo, trail and title dot. */
  activeVesselColor?: string | null;
  /** Where the loaded track came from. */
  trackSource?: 'none' | 'full' | 'saved';
  /**
   * trackId of the loaded track when it came from storage, else null.
   *
   * `trackSource` says a track is saved; this says WHICH, which is what lets the
   * Vessel Tracks section and the ON MAP bar mark the right row.
   */
  activeTrackId?: string | null;
  /** True while a track fetch is in flight. */
  trackLoading?: boolean;
  /**
   * `trackLoading`, delayed — what the UI should DRAW.
   *
   * A saved track is two serial network hops (signed URL, then the JSON off
   * storage), and how long that takes is not a constant — a short track on a
   * good connection can land before the eye has left the row. Drawing the
   * honest flag directly would blink a spinner on exactly those, which reads as
   * a glitch rather than as speed. This one waits the short loads out; see
   * `setTrackLoading`.
   */
  trackLoadingVisible?: boolean;
  /**
   * What the in-flight load is FOR, so the wait can name it.
   *
   * The whole point of the pending row: "1,284 pts · 24 h" for a named hull
   * reads as work happening on the thing that was clicked, where a bare spinner
   * reads as a frozen panel. Every field is already on the record the user
   * clicked — nothing here costs a request. Null when nothing is in flight.
   */
  pendingTrack?: PendingTrackRef | null;
  /** Span of the loaded/last-requested full track, in minutes. */
  trackSpanMinutes?: number | null;
  /** Remaining API credits in the wallet — drives the cost line + disabled states. */
  creditsRemaining?: number | null;
  /**
   * What one vessel track costs, from the sensor's own credit catalogue.
   *
   * Travels with the balance because it is read with it: a panel that showed a
   * shipped constant next to a live balance would price the action wrongly the
   * moment ops re-price it in ee-admin.
   */
  trackCost?: number;
  /**
   * TOI over — nothing on this task can be bought again, only refunded.
   *
   * Travels with the balance for the same reason `trackCost` does, and gates
   * the same controls: it is half of the one refusal ladder every priced
   * Voyage Radar control runs (`voyageSpendRefusal`).
   */
  toiEnded?: boolean;
  /** Previously purchased tracks available to replay for free. */
  savedTracks?: SavedTrackRef[];
  /**
   * While a track is on the map, fade the rest of the fleet out of the way.
   * User-controlled (the track bar's dim button) rather than implied by the
   * track: the surrounding traffic is sometimes the reason the track was bought.
   */
  dimOthers?: boolean;
  /**
   * How many vessels the dim would actually fade: everything in the snapshot on
   * the map EXCEPT the traced hull. Null when that is not known.
   *
   * Reported to the TRACK controller by the map, because a track can be replayed
   * with no snapshot on the map at all (from the Vessel Tracks panel) — and then
   * `dimOthers` has nothing to act on. It is what lets the dim button take itself
   * off a bar where it would do nothing.
   */
  othersOnMap?: number | null;

  // ── Voyage Radar located vessels (worldwide search) ──────────────────────────
  /**
   * Every vessel a search has put on the map (empty = none). Drives the single
   * search-history control: one button, one drawer listing them all — the
   * per-vessel toolbar it replaced narrated one hull with seven controls.
   */
  locatedVessels?: LocatedVesselRow[];
}

/**
 * One row in the search-history drawer (see VoyageVesselController): a vessel
 * a worldwide search located, whether placed this session or re-hydrated from
 * the sensor's `vesselHistory`.
 */
export interface LocatedVesselRow {
  mmsi: string;
  /** Broadcast name, or null for an anonymous hull (the row shows the MMSI). */
  name: string | null;
  /** ISO/UTC stamp of the fix the marker is drawn at. */
  timeUtc: string | null;
  /**
   * Fix older than 48 h — drawn hollow and captioned "last known", never
   * "latest". A six-day-old position must not wear the authority of a
   * twelve-minute-old one.
   */
  stale: boolean;
  /** Persisted in the sensor's vesselHistory — survives a reload. */
  kept: boolean;
  /** The search text that found it, when known — the row's provenance. */
  query?: string | null;
}

/**
 * The track a load in flight is about — everything the pending ON MAP row and
 * the spinning list row need to describe a wait truthfully.
 *
 * Built from the record the user clicked (a saved replay) or from the active
 * vessel plus the requested span (a paid fetch), so it is available on the same
 * frame as the click. Deliberately NOT a `SavedTrackRef`: a paid fetch has no
 * trackId yet and no stored point count, and this has to carry the vessel's
 * colour, which is a map fact rather than a stored one.
 */
export interface PendingTrackRef {
  /** Which stored track is being recalled; null for a paid fetch (no id yet). */
  trackId: string | null;
  /** 'saved' = free replay from storage, 'full' = paid operator fetch. */
  source: 'saved' | 'full';
  mmsi: string | null;
  /** Resolved display label — the same one the panel row shows. */
  label: string | null;
  /**
   * Whether that label is a real broadcast name or the "MMSI …" stand-in.
   *
   * Carried rather than re-derived: the label here is already RESOLVED, and
   * feeding it back through `resolveVesselIdentity` would read the stand-in as a
   * name and render "MMSI 636092371 · 636092371".
   */
  isNamed: boolean;
  /** The vessel's colour, so the pending rail matches the path about to appear. */
  color: string | null;
  /** Stored position count when known (saved replays only). */
  points: number | null;
  /** "24 h" — the span being recalled or bought. */
  spanLabel: string | null;
  /** "12 Jul 08:00 → 13 Jul 08:00", or null on a record with no window. */
  window: string | null;
}

/**
 * The replay a snapshot load in flight is about — what the pending ON MAP row
 * needs to describe the wait truthfully.
 *
 * The snapshot twin of `PendingTrackRef`, and deliberately much smaller: a
 * replay is about an archive rather than about a hull, so there is no identity,
 * colour or point count to carry — only how much of the record is coming and
 * which stretch of it. Both are known from `refreshHistory` on the same frame as
 * the click, so nothing here costs a request.
 */
export interface PendingPlaybackRef {
  /** Saved snapshots the load is about to fetch. */
  frames: number;
  /** "12 Jul 2026 – 28 Jul 2026", or null when the window cannot be described. */
  window: string | null;
}

/** A previously purchased vessel track that can be replayed at no cost. */
export interface SavedTrackRef {
  trackId: string;
  mmsi: string;
  vesselName?: string | null;
  from?: string;
  to?: string;
  count?: number;
  spanLabel?: string;
}

export const EMPTY_PLAYBACK_STATE: PlaybackState = {
  mode: 'idle',
  isPlaying: false,
  currentIndex: 0,
  stepCount: 0,
  stepLabels: [],
  speed: 60,
  showLabels: false,
  // On by default: the first thing a Voyage Radar pull puts on the map can be a
  // thousand hulls, and unclustered that is texture rather than a chart.
  clusterVessels: true,
  typeFilter: new Set<string>(),
  availableTypes: [],
  lastUpdated: null,
  loading: false,
  playbackLoading: false,
  playbackLoadingVisible: false,
  pendingPlayback: null,
  playbackRefreshId: null,
  playbackSkipped: 0,
  activeMmsi: null,
  activeVesselName: null,
  activeVesselColor: null,
  trackSource: 'none',
  activeTrackId: null,
  trackLoading: false,
  trackLoadingVisible: false,
  pendingTrack: null,
  trackSpanMinutes: null,
  creditsRemaining: null,
  // Deliberately absent: this contract is operator-agnostic and has no business
  // knowing Voyage Radar's price list. The panel falls back to the shipped
  // default until the VR controller reports the catalogue value.
  savedTracks: [],
  dimOthers: true,
  othersOnMap: null,
};

/**
 * Contract every sensor's playback source implements so the toolbar controls
 * stay operator-agnostic. AISstream's `AisstreamLayerService` already has all of
 * the transport methods — it just additionally exposes `state$`.
 */
export abstract class PlaybackController {
  /** Reactive state the controls subscribe to. */
  abstract readonly state$: Observable<PlaybackState>;
  /** Synchronous current state (for non-reactive reads). */
  abstract snapshot(): PlaybackState;

  // ── Transport ──────────────────────────────────────────────────────────────
  abstract togglePlay(): void;
  abstract play(): void;
  abstract pause(): void;
  abstract seek(index: number): void;
  abstract stepForward(): void;
  abstract stepBack(): void;
  abstract skipToStart(): void;
  abstract skipToEnd(): void;
  abstract setSpeed(n: number): void;

  // ── Display ──────────────────────────────────────────────────────────────────
  abstract toggleLabels(): void;
  abstract setTypeFilter(set: Set<string>): void;

  /**
   * Group nearby vessels into count bubbles, or draw every hull. Optional: only
   * the sensors whose rendering goes through a clustering pass implement it.
   */
  toggleClusters(): void {
    /* optional override */
  }

  // ── Mode transitions ──────────────────────────────────────────────────────────
  /** Load history for a UTC range and enter playback. Resolves with step count. */
  abstract enterPlayback(fromIso: string, toIso: string): Promise<number>;
  /** Return to the live view (X on the playback toolbar). */
  abstract resumeLive(): void;
  /** Tear down / close the toolbar entirely. */
  abstract close(): void;

  // ── Pull-based live (optional) ────────────────────────────────────────────────
  /**
   * User-initiated refresh for pull-based operators (Voyage Radar). Streaming
   * operators (AISstream) leave this as a no-op.
   */
  refresh(): void {
    /* optional override */
  }

  /**
   * Open the operator-specific historical date-range picker and load playback.
   * The controller owns this because the selectable window (TOI) is runtime/
   * collection-specific. Default no-op.
   */
  requestHistory(): void {
    /* optional override */
  }

  /** Load ALL saved history and enter playback (optional; Voyage Radar). */
  enterHistoryPlayback(): Promise<number> {
    return Promise.resolve(0);
  }

  /** Replace the available ship-type categories (optional; host computes from data). */
  setAvailableTypes(_types: PlaybackAvailableType[]): void {
    /* optional override */
  }

  // ── Voyage Radar track mode (optional) ───────────────────────────────────────
  /** Deselect the active vessel and tear down its track. Default no-op. */
  clearActiveVessel(): void {
    /* optional override */
  }

  /** Buy the dense operator track over `_minutes` (costs one historical fetch). */
  requestFullTrack(_minutes: number): void {
    /* optional override */
  }

  /** Replay an already-purchased track from storage (free). */
  requestSavedTrack(_trackId: string): void {
    /* optional override */
  }

  /** Fade / restore the vessels the loaded track is NOT about. Default no-op. */
  toggleDimOthers(): void {
    /* optional override */
  }
}
